import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { formFields } from "./edit_card";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../C5StaticData/edit-form-conf";
import { useTranslation } from "react-i18next";

const filterC5TimeSeriesSchema = z.object({
  c5_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterC5TimeSeriesFields = filterC5TimeSeriesSchema.keyof().enum;

export type FilterC5TimeSeriesSchema = z.infer<typeof filterC5TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterC5TimeSeriesSchema>[] = [
  {
    name: filterC5TimeSeriesFields.c5_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterC5TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterC5TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.C5_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterC5TimeSeriesFields.c5_static_datum_name_contains,
      label: t(`${tableName}.searchbox.c5_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_STATIC_DATA}`,
      id_column: formFields.c5_static_datum_id,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterC5TimeSeriesSchema>(
        assetDepFields,
        filterC5TimeSeriesFields.c5_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterC5TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterC5TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.C5_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.C5_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.C5,
      staticDataTableName: Tables.C5_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterC5TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_STATIC_DATA}`,
      id_column: formFields.c5_static_datum_id,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterC5TimeSeriesSchema>(
        assetDepFields,
        filterC5TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterC5TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterC5TimeSeriesFields.date_from },
      to: { name: filterC5TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterC5TimeSeriesFields.create_from },
      to: { name: filterC5TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterC5TimeSeriesSchema}
      client={Clients.C5}
      tableName={Tables.C5_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
